import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Lock, Settings, Users, HardHat } from 'lucide-react';
import { Btn } from '../components/admin/cockpit/Cockpit';
import GalleriesView from '../components/GalleriesView';
import UsersManagementPage from './UsersManagementPage';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { Toaster } from '@/components/ui/sonner';

const Dashboard = ({ token, username, role = 'admin', onLogout, backendUrl }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const isAdmin = role === 'admin';

  const axiosConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/galleries`, axiosConfig);
      setGalleries(response.data);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGalleries();
  }, []);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const isHomePage = location.pathname === '/';

  return (
    <div className="min-h-screen font-tac-body" style={{ background: '#EDEDF0' }}>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: '#fff', borderBottom: '1px solid #E4E4E9' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between" style={{ height: 64 }}>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/turbo-logo-color.png" alt="Turbo IQ" style={{ height: 26, width: 'auto' }} />
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {!isAdmin && (
                <span className="font-tac-mono inline-flex items-center gap-1.5" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', background: '#DBEAFE', color: '#1D4ED8', padding: '4px 9px', borderRadius: 6 }}>
                  <HardHat className="w-3 h-3" /> Crew
                </span>
              )}
              <div className="hidden sm:block" style={{ fontSize: 13, color: '#55555C' }}>
                Welcome, <span className="font-semibold" style={{ color: '#141414' }}>{username}</span>
              </div>
              {isAdmin && (
                <Btn variant="secondary" size="sm" onClick={() => navigate('/users')} data-testid="users-button">
                  <Users className="w-4 h-4" /><span className="hidden sm:inline">Users</span>
                </Btn>
              )}
              {isAdmin && (
                <Btn variant="secondary" size="sm" onClick={() => (window.location.href = '/settings')}>
                  <Settings className="w-4 h-4" /><span className="hidden sm:inline">Settings</span>
                </Btn>
              )}
              {isAdmin && (
                <Btn variant="secondary" size="sm" onClick={() => setShowChangePassword(true)}>
                  <Lock className="w-4 h-4" /><span className="hidden sm:inline">Password</span>
                </Btn>
              )}
              <Btn variant="secondary" size="sm" onClick={handleLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Logout</span>
              </Btn>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route
            path="/"
            element={
              <GalleriesView
                galleries={galleries}
                onRefresh={fetchGalleries}
                axiosConfig={axiosConfig}
                backendUrl={backendUrl}
                userRole={role}
              />
            }
          />
          {isAdmin && (
            <Route
              path="/users"
              element={
                <UsersManagementPage
                  axiosConfig={axiosConfig}
                  backendUrl={backendUrl}
                  galleries={galleries}
                />
              }
            />
          )}
        </Routes>
      </main>

      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </div>
  );
};

export default Dashboard;
